// DME orders — create, review, and move through the stages.
//
// Items come from dme_fee_schedule, so what can be ordered, what it bills at,
// and how much a payer allows per month live in one editable table rather
// than in this file. Creating an order requires everything Georgia's
// medical-necessity review asks for; moving it forward goes through
// AdvanceOrderModal, which asks for whatever the next stage needs.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { format } from 'date-fns';
import { StatTile } from '../../components/StatTile';
import DataTable from '../../components/DataTable';
import AdvanceOrderModal from '../../components/dme/AdvanceOrderModal';
import PhysicianPicker from '../../components/directory/PhysicianPicker';
import { getCareTeamDetailed } from '../../lib/directory';
import { payerLabel, CLOSED_STATUS_FILTER } from '../../lib/serviceModel';
import {
  STATUS_PIPELINE, SIDE_STATUSES, CATEGORIES, statusInfo, statusIndex, nextStatus, sideTargets, DME_PAYER,
  loadFeeSchedule, feeItemFor, expectedAmount, coverageCheck, money, ageOn, logOrderEvent,
} from '../../lib/dmeWorkflow';

const lastFirst = (c) => [c.last_name, c.first_name].filter(Boolean).join(', ');
const catColor = (c) => ({ incontinence: 'var(--accent)', ostomy: 'var(--warn)', catheter: 'var(--info)' }[c] || 'var(--text2)');
const catBg    = (c) => ({ incontinence: 'var(--accent-bg)', ostomy: 'var(--warn-bg)', catheter: 'var(--info-bg)' }[c] || 'var(--bg2)');

const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, fontWeight: 500, color: 'var(--text2)', marginBottom: 4, display: 'block' };

const FG = ({ label, children, half, required, hint }) => (
  <div style={{ flex: half ? '1 1 calc(50% - 6px)' : '1 1 100%', minWidth: 0 }}>
    <label style={labelStyle}>{label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}</label>
    {children}
    {hint && <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3 }}>{hint}</div>}
  </div>
);
const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 6 }}>{children}</div>
);

function StatusBadge({ status }) {
  const s = statusInfo(status);
  return <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>{s.short}</span>;
}

function StatusTimeline({ status }) {
  const current = statusIndex(status);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '10px 0' }}>
      {STATUS_PIPELINE.map((s, i) => (
        <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <div style={{ fontSize: 10, fontWeight: 500, padding: '3px 8px', borderRadius: 20,
            background: i <= current ? s.bg : 'var(--bg2)', color: i <= current ? s.color : 'var(--text3)',
            border: i === current ? `1.5px solid ${s.color}` : '1.5px solid transparent' }}>
            {i < current ? '✓ ' : ''}{s.label}
          </div>
          {i < STATUS_PIPELINE.length - 1 && <span style={{ fontSize: 9, color: 'var(--text3)' }}>›</span>}
        </div>
      ))}
    </div>
  );
}

function ModalBackdrop({ onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, width: '100%', maxWidth: 720, boxShadow: '0 24px 48px rgba(0,0,0,0.25)', flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );
}

const blankForm = {
  client_id: '', client_name: '', client_ref: '',
  product_category: 'incontinence', hcpcs_code: '', hcpcs_description: '', product_description: '',
  quantity_ordered: '', quantity_unit: 'each', frequency_per_day: '',
  physician_id: null, prescribing_physician: '', physician_npi: '', qualifying_diagnosis: '', icd10_code: '', rx_date: '',
  delivery_address: '', is_recurring: true, recurring_frequency: 'monthly', notes: '',
};

export default function Orders() {
  const { profile } = useAuth();
  const [params, setParams] = useSearchParams();

  const [orders, setOrders]       = useState([]);
  const [clients, setClients]     = useState([]);
  const [schedule, setSchedule]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected]   = useState(null);
  const [events, setEvents]       = useState([]);
  const [advancing, setAdvancing] = useState(null);   // { order, target? } being advanced (modal)
  const [showNew, setShowNew]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');
  const [form, setForm]           = useState(blankForm);

  const load = useCallback(async () => {
    setLoading(true);
    const [ordRes, clRes, sched] = await Promise.all([
      supabase.from('dme_orders').select('*').eq('is_test', false).order('created_at', { ascending: false }),
      // Eligibility for a DME order is the DME service line, not a GAPP-shaped status.
      supabase.from('clients')
        .select('id, client_id, first_name, last_name, date_of_birth, address, city, state, zip, current_status, service_type')
        .contains('service_lines', ['dme'])
        .not('current_status', 'in', CLOSED_STATUS_FILTER)
        .order('last_name'),
      loadFeeSchedule(),
    ]);
    setOrders(ordRes.data || []);
    setClients(clRes.data || []);
    setSchedule(sched);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep link: /dme/orders?client=<uuid> opens a new order for that client.
  useEffect(() => {
    const cid = params.get('client');
    if (cid && clients.length && !showNew) {
      handleClientChange(cid).then(() => setShowNew(true));
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, clients]);

  useEffect(() => {
    if (!selected) { setEvents([]); return; }
    supabase.from('dme_order_events').select('*').eq('order_id', selected.id).order('created_at', { ascending: false })
      .then(({ data }) => setEvents(data || []));
  }, [selected]);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const itemsForCategory = useMemo(() => schedule.filter(x => x.category === form.product_category), [schedule, form.product_category]);
  const feeItem = useMemo(() => feeItemFor(schedule, form.hcpcs_code), [schedule, form.hcpcs_code]);
  const formClient = useMemo(() => clients.find(c => c.id === form.client_id) || null, [clients, form.client_id]);
  const coverage = useMemo(() => (feeItem && formClient ? coverageCheck(form, formClient, feeItem) : { blocking: [], warnings: [] }), [form, formClient, feeItem]);
  const expected = expectedAmount(feeItem, form.quantity_ordered);

  const [pickedDoc, setPickedDoc] = useState(null);
  const docName = (p) => `${p.first_name} ${p.last_name}`.trim() + (p.credentials ? `, ${p.credentials}` : '');
  const pickDoc = (p) => {
    setPickedDoc(p);
    setForm(f => ({ ...f, physician_id: p?.id || null, prescribing_physician: p ? docName(p) : '', physician_npi: p ? (p.npi || '') : '' }));
  };

  // Choosing a client also pulls in what the intake form already captured, so
  // the coordinator confirms rather than re-keys.
  const handleClientChange = async (id) => {
    const c = clients.find(x => x.id === id);
    if (!c) { setForm(f => ({ ...f, client_id: '', client_name: '', client_ref: '' })); return; }
    const addr = c.address ? [c.address, c.city, c.state, c.zip].filter(Boolean).join(', ') : '';
    const { data: intake } = await supabase.from('intake_submissions')
      .select('dme_product_categories, dme_qualifying_diagnosis, dme_prescribing_physician, dme_physician_npi, dme_delivery_address_same, dme_delivery_address, doctor_name')
      .eq('client_id_created', c.client_id).order('submitted_at', { ascending: false }).limit(1).maybeSingle();
    // The client's ordering physician (or PCP) from the directory beats
    // whatever the intake form said.
    const { data: team } = await getCareTeamDetailed(id);
    const doc = (team || []).find(m => m.role === 'ordering')?.physician || (team || []).find(m => m.role === 'pcp')?.physician || null;
    setPickedDoc(doc);
    setForm(f => ({
      ...f,
      client_id: id, client_name: lastFirst(c), client_ref: c.client_id,
      delivery_address: f.delivery_address || (intake?.dme_delivery_address_same === false && intake?.dme_delivery_address) || addr,
      physician_id: doc?.id || null,
      prescribing_physician: doc ? docName(doc) : (f.prescribing_physician || intake?.dme_prescribing_physician || intake?.doctor_name || ''),
      physician_npi: doc ? (doc.npi || '') : (f.physician_npi || intake?.dme_physician_npi || ''),
      qualifying_diagnosis: f.qualifying_diagnosis || intake?.dme_qualifying_diagnosis || '',
      product_category: (intake?.dme_product_categories || [])[0] || f.product_category,
    }));
  };

  const handleItemChange = (code) => {
    const item = feeItemFor(schedule, code);
    setForm(f => ({
      ...f, hcpcs_code: code,
      hcpcs_description: item?.description || '',
      product_description: f.product_description || item?.description || '',
      quantity_unit: item?.unit || 'each',
      quantity_ordered: f.quantity_ordered || item?.allowable_qty || '',
    }));
  };

  const createErrors = () => {
    const e = [];
    if (!form.client_id) e.push('Select a client.');
    if (!form.hcpcs_code) e.push('Select an item.');
    if (!form.quantity_ordered || Number(form.quantity_ordered) <= 0) e.push('Enter a quantity.');
    if (!form.frequency_per_day) e.push('Enter how many times per day it was ordered.');
    if (!form.prescribing_physician.trim()) e.push('Enter the prescribing physician.');
    if (!/^\d{10}$/.test(form.physician_npi.trim())) e.push('Prescriber NPI must be 10 digits.');
    if (!form.qualifying_diagnosis.trim()) e.push('Enter the qualifying diagnosis.');
    if (!form.icd10_code.trim()) e.push('Enter the ICD-10 code.');
    if (!form.rx_date) e.push('Enter the prescription date.');
    if (!form.delivery_address.trim()) e.push('Enter a delivery address.');
    e.push(...coverage.blocking);
    return e;
  };

  const handleSave = async () => {
    const errs = createErrors();
    if (errs.length) { setSaveError(errs.join(' ')); return; }
    setSaving(true); setSaveError('');
    const payload = {
      ...form,
      quantity_ordered: Number(form.quantity_ordered),
      frequency_per_day: Number(form.frequency_per_day),
      physician_npi: form.physician_npi.trim(),
      icd10_code: form.icd10_code.trim().toUpperCase(),
      status: 'referral_received',
      order_date: format(new Date(), 'yyyy-MM-dd'),
      cmn_required: !!feeItem?.cmn_required,
      expected_amount: expected,
      created_by: profile?.id || null,
      created_by_name: profile?.full_name || '',
    };
    const { data, error } = await supabase.from('dme_orders').insert(payload).select().single();
    if (error) { setSaveError(error.message); setSaving(false); return; }
    await logOrderEvent(data.id, `Order created${coverage.warnings.length ? ' — ' + coverage.warnings.join(' ') : ''}`, profile);
    setShowNew(false); setForm(blankForm); setPickedDoc(null); setSaving(false);
    load();
  };

  // There is no DME 837P. Every DME claim goes to CareSource and is filed on
  // CareSource's side; the order records the claim number and what came back.
  // The export that used to live here built a file addressed to GAMMIS, which
  // was wrong for every order and, once the SFTP transport went live, a trap.

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    if (q && !o.client_name?.toLowerCase().includes(q) && !o.order_ref?.toLowerCase().includes(q) && !o.hcpcs_code?.toLowerCase().includes(q)) return false;
    if (filterCat && o.product_category !== filterCat) return false;
    if (filterStatus && o.status !== filterStatus) return false;
    return true;
  });

  const statCounts = {
    total:    orders.length,
    active:   orders.filter(o => o.status !== 'claim_paid').length,
    authPend: orders.filter(o => ['referral_received', 'auth_pending'].includes(o.status)).length,
    cmnPend:  orders.filter(o => o.status === 'cmn_pending').length,
    blocked:  orders.filter(o => o.resupply_blocked_reason).length,
  };

  const columns = [
    { accessorKey: 'order_ref', header: 'Order Ref', size: 150, meta: { mono: true, mobile: 'secondary' }, cell: info => info.getValue() || '—' },
    { accessorKey: 'client_name', header: 'Client', size: 170, meta: { mobile: 'primary' }, cell: info => info.getValue() },
    { id: 'category', accessorKey: 'product_category', header: 'Category', size: 110, meta: { mobile: 'secondary' },
      cell: info => { const c = info.getValue(); return <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: catBg(c), color: catColor(c), textTransform: 'capitalize' }}>{c}</span>; } },
    { accessorKey: 'hcpcs_code', header: 'HCPCS', size: 90, meta: { mono: true, mobile: 'hidden' }, cell: info => info.getValue() || '—' },
    { accessorKey: 'quantity_ordered', header: 'Qty', size: 70, meta: { align: 'right', mobile: 'hidden' }, cell: info => info.getValue() ?? '—' },
    { accessorKey: 'expected_amount', header: 'Expected', size: 90, meta: { align: 'right', mobile: 'hidden' }, cell: info => money(info.getValue()) },
    { accessorKey: 'supplier_name', header: 'Supplier', size: 130, meta: { mobile: 'hidden' }, cell: info => info.getValue() || '—' },
    { accessorKey: 'status', header: 'Status', size: 120, meta: { mobile: 'primary' },
      cell: info => { const o = info.row.original; return <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><StatusBadge status={info.getValue()} />{o.resupply_blocked_reason && <span title={o.resupply_blocked_reason} style={{ fontSize: 10, color: 'var(--danger)' }}>⚠ resupply</span>}</span>; } },
    { accessorKey: 'order_date', header: 'Ordered', size: 110, meta: { mobile: 'secondary' }, cell: info => info.getValue() ? format(new Date(info.getValue() + 'T00:00:00'), 'MMM d, yyyy') : '—' },
    { id: 'actions', header: '', size: 130, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => { const o = info.row.original; const nx = nextStatus(o.status);
        return (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            {nx && <button className="btn btn-outline btn-sm" onClick={e => { e.stopPropagation(); setAdvancing({ order: o }); }} style={{ fontSize: 10, padding: '3px 9px', whiteSpace: 'nowrap' }}>→ {statusInfo(nx).short}</button>}
            {sideTargets(o.status).map(t => (
              <button key={t} className="btn btn-outline btn-sm" onClick={e => { e.stopPropagation(); setAdvancing({ order: o, target: t }); }}
                style={{ fontSize: 10, padding: '3px 9px', whiteSpace: 'nowrap', borderColor: statusInfo(t).color, color: statusInfo(t).color }}>{statusInfo(t).short}</button>
            ))}
          </span>
        ); } },
  ];

  const clientAge = formClient ? ageOn(formClient.date_of_birth) : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Equipment Orders</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Incontinence · Ostomy · Catheter</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => { setForm(blankForm); setPickedDoc(null); setSaveError(''); setShowNew(true); }}>+ New Order</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,minmax(0,1fr))', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'Total',           val: statCounts.total,    tone: 'neutral' },
          { label: 'Active',          val: statCounts.active,   tone: 'neutral' },
          { label: 'Needs Auth',      val: statCounts.authPend, tone: statCounts.authPend > 0 ? 'warn' : 'neutral' },
          { label: 'CMN Pending',     val: statCounts.cmnPend,  tone: statCounts.cmnPend > 0 ? 'warn' : 'neutral' },
          { label: 'Resupply Blocked',val: statCounts.blocked,  tone: statCounts.blocked > 0 ? 'danger' : 'neutral' },
        ].map(s => <StatTile key={s.label} label={s.label} value={s.val} tone={s.tone} compact />)}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search client, ref, HCPCS…" style={{ ...inputStyle, flex: '1 1 200px', maxWidth: 300 }} />
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...inputStyle, flex: '0 0 auto', width: 'auto' }}>
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, flex: '0 0 auto', width: 'auto' }}>
          <option value="">All statuses</option>
          {[...STATUS_PIPELINE, ...SIDE_STATUSES].map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      <div className="card">
        {!loading && filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '60px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📦</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>No orders yet</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Create the first order, or start one from a DME client's referral card on the Pipeline.</div>
            <button className="btn btn-primary btn-sm" onClick={() => { setForm(blankForm); setPickedDoc(null); setShowNew(true); }}>+ New Order</button>
          </div>
        ) : (
          <DataTable tableId="dme-orders" columns={columns} data={filtered} getRowId={o => o.id} onRowClick={setSelected}
            loading={loading} emptyMessage="No orders match your filters." maxHeight="calc(100vh - 260px)" />
        )}
      </div>

      {advancing && (
        <AdvanceOrderModal order={advancing.order} target={advancing.target} onClose={() => setAdvancing(null)}
          onAdvanced={(updated) => { setSelected(s => (s && s.id === updated.id ? updated : s)); load(); }} />
      )}

      {/* ── Detail ── */}
      {selected && (
        <ModalBackdrop onClose={() => setSelected(null)}>
          <div style={{ padding: '18px 24px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{selected.client_name}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                <span style={{ fontFamily: 'ui-monospace, monospace' }}>{selected.order_ref}</span> · {selected.client_ref}
                {selected.is_recurring && <span style={{ marginLeft: 8, fontSize: 9, background: 'var(--accent-bg)', color: 'var(--accent)', padding: '1px 6px', borderRadius: 3, fontWeight: 500 }}>Recurring {selected.recurring_frequency}</span>}
                {selected.parent_order_id && <span style={{ marginLeft: 6, fontSize: 9, background: 'var(--bg2)', color: 'var(--text2)', padding: '1px 6px', borderRadius: 3 }}>Resupply</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {nextStatus(selected.status) && (
                <button className="btn btn-primary btn-sm" onClick={() => setAdvancing({ order: selected })}>→ {statusInfo(nextStatus(selected.status)).label}</button>
              )}
              {sideTargets(selected.status).map(t => (
                <button key={t} className="btn btn-outline btn-sm" style={{ borderColor: statusInfo(t).color, color: statusInfo(t).color }}
                  onClick={() => setAdvancing({ order: selected, target: t })}>{statusInfo(t).label}</button>
              ))}
              <button className="btn btn-outline btn-sm" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
          <div style={{ padding: '14px 24px' }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Stages</div>
            <StatusTimeline status={selected.status} />
            {selected.resupply_blocked_reason && (
              <div style={{ margin: '8px 0', padding: '10px 12px', background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, fontSize: 12.5, color: 'var(--danger)' }}>
                <b>Resupply refused:</b> {selected.resupply_blocked_reason}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', marginTop: 12 }}>
              {[
                ['Category',        <span style={{ textTransform: 'capitalize', color: catColor(selected.product_category), fontWeight: 500 }}>{selected.product_category}</span>],
                ['Item',            `${selected.hcpcs_code || '—'} · ${selected.hcpcs_description || selected.product_description || ''}`],
                ['Quantity',        `${selected.quantity_ordered ?? '—'} ${selected.quantity_unit || ''}${selected.frequency_per_day ? ` · ${selected.frequency_per_day}/day` : ''}`],
                ['Expected claim',  money(selected.expected_amount)],
                ['Prescriber',      selected.prescribing_physician ? `${selected.prescribing_physician} · NPI ${selected.physician_npi || '—'}` : '—'],
                ['Diagnosis',       selected.qualifying_diagnosis ? `${selected.qualifying_diagnosis} · ${selected.icd10_code || '—'}` : '—'],
                ['Prescription',    selected.rx_date ? <span>{selected.rx_date}{selected.rx_document_url && <> · <a href={selected.rx_document_url} target="_blank" rel="noreferrer">file</a></>}</span> : '—'],
                ['Authorization',   selected.auth_number ? `${selected.auth_number} · ${selected.auth_start_date || '?'} → ${selected.auth_end_date || '?'}` : '—'],
                ['CMN',             selected.cmn_required === false ? 'Not required' : selected.cmn_id ? 'Linked' : '—'],
                ['Supplier',        selected.supplier_name || '—'],
                ['Tracking',        selected.tracking_number || '—'],
                ['Shipped',         selected.shipped_date || '—'],
                ['Delivered',       selected.delivered_date || '—'],
                ['Proof of delivery', selected.pod_received_by ? <span>{selected.pod_received_by} · {selected.pod_delivered_date} · {selected.quantity_delivered ?? '?'} {selected.quantity_unit}{selected.pod_document_url && <> · <a href={selected.pod_document_url} target="_blank" rel="noreferrer">slip</a></>}</span> : '—'],
                ['Payer',           payerLabel(DME_PAYER)],
                ['Claim',           selected.claim_number ? `${selected.claim_number} · ${money(selected.claim_amount)} · ${selected.claim_submitted_date || ''}` : '—'],
                ['Denied',          selected.claim_denied_date ? `${selected.claim_denied_date}${selected.claim_denial_reason ? ` · ${selected.claim_denial_reason}` : ''}` : '—'],
                ['Paid',            selected.claim_paid_date ? `${money(selected.paid_amount ?? selected.claim_amount)} · ${selected.claim_paid_date}` : '—'],
                ['Next resupply',   selected.next_resupply_date || (selected.is_recurring ? 'after delivery' : '—')],
                ['Delivery address',selected.delivery_address || '—'],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '0.5px solid var(--border)', gap: 10 }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>{label}</span>
                  <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500, textAlign: 'right' }}>{val}</span>
                </div>
              ))}
            </div>
            {selected.notes && <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text2)' }}><b>Notes:</b> {selected.notes}</div>}

            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>History</div>
              {events.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>No events recorded.</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {events.map(ev => (
                    <div key={ev.id} style={{ fontSize: 12, display: 'flex', gap: 10, padding: '5px 0', borderBottom: '0.5px solid var(--border)' }}>
                      <span style={{ color: 'var(--text3)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{format(new Date(ev.created_at), 'MMM d, h:mm a')}</span>
                      <span style={{ color: 'var(--text)' }}>
                        {ev.to_status && ev.to_status !== ev.from_status ? <b>{statusInfo(ev.to_status).label}</b> : null}
                        {ev.note ? <span style={{ color: 'var(--text2)' }}>{ev.to_status && ev.to_status !== ev.from_status ? ' — ' : ''}{ev.note}</span> : null}
                      </span>
                      <span style={{ marginLeft: 'auto', color: 'var(--text3)', flexShrink: 0 }}>{ev.performed_by_name || ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* ── New order ── */}
      {showNew && (
        <ModalBackdrop onClose={() => setShowNew(false)}>
          <div style={{ padding: '18px 24px', borderBottom: '0.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>New Equipment Order</div>
            <button className="btn btn-outline btn-sm" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
          <div style={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '70vh', overflowY: 'auto' }}>

            <SectionLabel>Client</SectionLabel>
            <FG label="Client" required hint="Lists clients whose Services include DME / Supplies. If someone is missing, add that service line on their client record.">
              <select value={form.client_id} onChange={e => handleClientChange(e.target.value)} style={inputStyle}>
                <option value="">— Choose client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{lastFirst(c)} ({c.client_id}) · {c.current_status}</option>)}
              </select>
            </FG>
            {formClient && (
              <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>
                Age {clientAge ?? '—'} · Coverage <b style={{ textTransform: 'capitalize' }}>{(formClient.service_type || '').replace('_', ' ')}</b> · Claims go to <b>{payerLabel(DME_PAYER)}</b>
                {clientAge === null && <span style={{ color: 'var(--warn)' }}> · no date of birth on record</span>}
              </div>
            )}

            <SectionLabel>Item</SectionLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <FG label="Category" required half>
                <select value={form.product_category} onChange={e => { set('product_category')(e.target.value); setForm(f => ({ ...f, hcpcs_code: '', hcpcs_description: '', product_description: '' })); }} style={inputStyle}>
                  {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </FG>
              <FG label="HCPCS item" required half hint={feeItem ? `${money(feeItem.rate)}/${feeItem.unit} · allowable ${feeItem.allowable_qty ?? '—'}/${feeItem.allowable_period}${feeItem.min_age !== null ? ` · ages ${feeItem.min_age}–${feeItem.max_age}` : ''}${feeItem.cmn_required ? ' · CMN required' : ''}` : 'From DME → Items & Charges'}>
                <select value={form.hcpcs_code} onChange={e => handleItemChange(e.target.value)} style={inputStyle}>
                  <option value="">— Select item —</option>
                  {itemsForCategory.map(i => <option key={i.id} value={i.hcpcs_code}>{i.hcpcs_code} — {i.description}</option>)}
                </select>
              </FG>
              <FG label="Product description" hint="Brand / size as it will appear to the supplier">
                <input value={form.product_description} onChange={e => set('product_description')(e.target.value)} style={inputStyle} />
              </FG>
              <FG label="Quantity" required half hint={feeItem?.allowable_qty && Number(form.quantity_ordered) > feeItem.allowable_qty ? `Over the ${feeItem.allowable_qty}/${feeItem.allowable_period} allowable — the PA must justify it` : undefined}>
                <input type="number" min="1" value={form.quantity_ordered} onChange={e => set('quantity_ordered')(e.target.value)} style={inputStyle} />
              </FG>
              <FG label="Uses per day (as ordered)" required half>
                <input type="number" min="0" step="0.5" value={form.frequency_per_day} onChange={e => set('frequency_per_day')(e.target.value)} style={inputStyle} />
              </FG>
              <FG label="Order type" half>
                <select value={form.is_recurring ? 'yes' : 'no'} onChange={e => set('is_recurring')(e.target.value === 'yes')} style={inputStyle}>
                  <option value="yes">Recurring monthly</option>
                  <option value="no">One-time</option>
                </select>
              </FG>
              <FG label="Expected claim" half>
                <div style={{ ...inputStyle, background: 'var(--bg2)', fontWeight: 600 }}>{money(expected)}</div>
              </FG>
            </div>

            <SectionLabel>Medical necessity</SectionLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <FG label="Prescriber — from the Provider Directory">
                <PhysicianPicker value={pickedDoc} onChange={pickDoc} placeholder="Search physician by name, NPI or practice…" />
              </FG>
              <FG label="Prescribing physician / NP / PA" required half>
                <input value={form.prescribing_physician} onChange={e => set('prescribing_physician')(e.target.value)} style={inputStyle} disabled={!!pickedDoc} />
              </FG>
              <FG label="Prescriber NPI" required half hint="10 digits">
                <input value={form.physician_npi} onChange={e => set('physician_npi')(e.target.value)} style={inputStyle} maxLength={10} disabled={!!pickedDoc} />
              </FG>
              <FG label="Qualifying diagnosis" required half>
                <input value={form.qualifying_diagnosis} onChange={e => set('qualifying_diagnosis')(e.target.value)} style={inputStyle} placeholder="e.g. Cerebral palsy" />
              </FG>
              <FG label="ICD-10 code" required half>
                <input value={form.icd10_code} onChange={e => set('icd10_code')(e.target.value)} style={inputStyle} placeholder="G80.9" />
              </FG>
              <FG label="Prescription / LMN date" required half hint="Signed within the last 12 months. The file itself is attached when the order leaves Referral.">
                <input type="date" value={form.rx_date} onChange={e => set('rx_date')(e.target.value)} style={inputStyle} />
              </FG>
            </div>

            <SectionLabel>Delivery</SectionLabel>
            <FG label="Delivery address" required hint={formClient && !formClient.address ? 'No street address on the client record — enter one here and add it to the client.' : undefined}>
              <input value={form.delivery_address} onChange={e => set('delivery_address')(e.target.value)} style={inputStyle} />
            </FG>
            <FG label="Notes">
              <textarea rows={2} value={form.notes} onChange={e => set('notes')(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
            </FG>

            {coverage.blocking.length > 0 && (
              <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: 'var(--danger)' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>{coverage.blocking.map((b, i) => <li key={i}>{b}</li>)}</ul>
              </div>
            )}
            {coverage.warnings.length > 0 && (
              <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--warn)', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: 'var(--warn)' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>{coverage.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            )}
            {saveError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{saveError}</div>}
          </div>
          <div style={{ padding: '12px 24px', borderTop: '0.5px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setShowNew(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>{saving ? 'Creating…' : 'Create Order'}</button>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}
